// /**
//  * Orders details - list
//  */

// React
import React, { useEffect, useState } from 'react';

// UI
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Tooltip,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiChevronDown,
  mdiChevronUp,
} from '@mdi/js';

// Helper
import {
  translate,
  checkMatchMedia,
} from '../../utils/helpers';

// Type
import { Item } from './interfaces';
import { useForm } from 'react-hook-form';
import RonadIcons from '../../components/iconComponent';

type Data = Item;
type Props = {
  setOpenUnits: () => void;
  packItem: (serial: string) => void;
  serialNumber: boolean;
  data: any;
};
type Ord = 'asc' | 'desc';
interface HeadCell {
  id: keyof Data;
  label: string;
}
interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  setOpenUnits: () => void;
  packItem: (serial: string) => void;
  order: Ord;
  orderBy: string;
  serialNumber: boolean;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.primary.main,
      whiteSpace: 'nowrap',
      color: theme.palette.common.white,
      '@media (max-width: 768px)': {
        fontSize: 10,
      },
    },
    body: {
      whiteSpace: 'nowrap',
      fontSize: 14,
    },
  })
)(TableCell);

const StyledTableRow = withStyles((theme: Theme) =>
  createStyles({
    root: {
      '&:nth-of-type(even)': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  })
)(TableRow);
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      '& td.MuiTableCell-root': {
        padding: '5px',
      },
    },
    parentRow: {
      '& > td': {
        fontWeight: 500,
        fontSize: '15px',
      },
    },
    paper: {
      width: '100%',
    },
    table: {
      // minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    textArea: {
      width: '100%',
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    container: {
      maxHeight: 540,
      borderRadius: '4px',
    },
    // Fully visible for active icons
    activeSortIcon: {
      opacity: 1,
      color: `${theme.palette.secondary.main}!important`,
    },
    // Half visible for inactive icons
    inactiveSortIcon: {
      opacity: 0.4,
    },
    detail: {
      backgroundColor: theme.palette.gray.main,
      padding: '5px',
      width: '100%',
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'center',
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
  })
);

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const classes = useStyles();
  const { order, orderBy, onRequestSort } = props;
  const { register, handleSubmit, setValue, getValues } = useForm({});
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  const headCells: HeadCell[] = [
    // {id: 'status', label: 'وضعیت'},
    { id: 'productName', label: 'نام کالا' },
    { id: 'count', label: 'تعداد' },
    { id: 'productId', label: 'شناسه کالا' },
    { id: 'variantId', label: 'شناسه تنوع کالا' },
    { id: 'productCode', label: 'کد کالا' },
    {id: 'dkpc', label: 'کد دیجی‌کالا'},
    // { id: 'variantCode', label: 'کد تنوع کالا' },
    // { id: 'fulfillmentVariantCode', label: 'کد مرکز پردازش' },
  ];

  const onSubmit = (data: any) => {
    props.packItem(data.serial);
  };

  useEffect(() => {
    setValue('serial', '');
  }, [props.serialNumber]);

  return (
    <TableHead>
      <StyledTableRow>
        {/* <StyledTableCell /> */}
        {headCells.map(headCell => (
          <StyledTableCell
            key={headCell.id}
            align="center"
            sortDirection={orderBy === headCell.id ? order : false}
            data-cy={headCell.id}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              classes={{
                // Override with the active class if this is the selected column or inactive otherwise
                icon:
                  orderBy === headCell.id
                    ? classes.activeSortIcon
                    : classes.inactiveSortIcon,
              }}
              IconComponent={() => (
                <Icon
                  path={
                    orderBy === headCell.id && order === 'desc'
                      ? mdiChevronDown
                      : mdiChevronUp
                  }
                  size={0.7}
                  className={
                    orderBy === headCell.id
                      ? classes.activeSortIcon
                      : classes.inactiveSortIcon
                  }
                />
              )}
            >
              {headCell.label}
            </TableSortLabel>
          </StyledTableCell>
        ))}
        <StyledTableCell>
          <IconButton
            aria-label="expand row"
            size="medium"
            onClick={props.setBodyOpen}
          // disabled={row.inventoryItems.length === 0}
          >
            {props.open ? (
              <Icon
                path={mdiChevronUp}
                size={1}
                color='white'
              // color={
              //   row.inventoryItems.length === 0
              //     ? '#d4d2da'
              //     : theme.palette.secondary.main
              // }
              />
            ) : (
              <Icon
                path={mdiChevronDown}
                size={1}
                color='white'
              // color={
              //   row.inventoryItems.length === 0
              //     ? '#d4d2da'
              //     : theme.palette.secondary.main
              // }
              />
            )}
          </IconButton>
        </StyledTableCell>
        {/* <StyledTableCell align="right">
          <form style={{ width: '320px'}} onSubmit={handleSubmit(onSubmit)}>
            <Grid
              className={classes.detail}
              container
              spacing={2}
              alignItems="center"
            >
              <Grid item xs={9} alignItems="center">
                <span style={{ margin: '0 10px'}}>
                  <Icon path={mdiBarcodeScan} size={1} color="black" />
                </span>
                <span>
                  <TextField
                    fullWidth
                    id="serial"
                    name="serial"
                    size="small"
                    inputRef={register({
                      required: true,
                    })}
                    autoFocus
                    label="شماره سریال را وارد کنید"
                  />
                </span>
              </Grid>
              <Grid item xs={3}>
                <IconButton
                  aria-label="details"
                  type="submit"
                  size="small"
                  title="بسته بندی آیتم"
                  data-cy="details"
                >
                  <Icon
                    path={mdiPackageVariant}
                    size={1}
                    color={theme.palette.info.main}
                  />
                </IconButton>
              </Grid>
            </Grid>
          </form>
        </StyledTableCell> */}
      </StyledTableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('productId');
  const [open, setOpen] = useState(true);
  // const [dense] = React.useState(false);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <TableContainer className={classes.container}>
          <Table
            stickyHeader
            className={classes.table}
            aria-labelledby="tableTitle"
            size={checkMatchMedia('(max-width: 1024px)') ? 'small' : 'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              serialNumber={props.serialNumber}
              onRequestSort={handleRequestSort}
              setOpenUnits={() => props.setOpenUnits()}
              packItem={serial => props.packItem(serial)}
              rowCount={props.data.length}
              setBodyOpen={() => { setOpen(!open) }}
              open={open}
            />
            {open && (
              <TableBody>
                {stableSort(props.data, getComparator(order, orderBy))
                  .slice()
                  .map((row: any, index: number) => {
                    const labelId = `enhanced-table-checkbox-${index}`;
                    return (
                      <Row
                        key={index}
                        row={row}
                        index={index}
                        labelId={labelId}
                      />
                    );
                  })}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}
export const HtmlTooltip = withStyles((theme: Theme) => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 280,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
    textAlign: 'left',
    direction: 'rtl',
    padding: '1rem'
  },
}))(Tooltip);

function Row(props: { row: any; labelId: string; index: number }) {
  const { row, labelId, index } = props;
  const classes = useStyles();
  const dkpcs = row.externalProductCodeDTOS?.map(el => el.marketplaceType === 'DIGIKALA' ? el.externalCode : '').join(', ')


  // let variantName = row.type === 'PRODUCT'
  //   ? row.productName
  //   : translate(row.type);
  // for (let i = 0; i <= 2 && row.featureAndValues[i]; i++) {
  //   variantName = variantName.concat(
  //     ' - ',
  //     row.featureAndValues[i].key,
  //     ' : ',
  //     row.featureAndValues[i].value
  //   );
  // }

  // if (row.sellerName || row.providerName) {
  //   const sellerName = row.sellerName ?? row.providerName;
  //   variantName = variantName.concat(' ( ', sellerName, ' )');
  // }

  return (
    <>
      {/* <StyledTableRow className={classes.parentRow} key={index} tabIndex={-1}> */}
      <StyledTableRow className={classes.parentRow} key={index}>
        {/* <TableCell>
          <IconButton
            aria-label="details"
            size="small"
            title="جمع آوری آیتم"
            data-cy="details"
            disabled={true}
          >
            <Icon
              path={
                row.status === 'PACKAGED' ? mdiCheckBoxOutline : mdiSquareOutline
              }
              size={1}
              color={
                row.status === 'PACKAGED' ? theme.palette.green.main : '#383836'
              }
            />
          </IconButton>
        </TableCell>
        <TableCell align="center" data-cy="row-id">
          {translate(row.status)}
        </TableCell> */}
        <TableCell id={labelId} scope="row" align="center">
          {row.type === 'PRODUCT'
            ? row.productName
            : translate(row.type, 'inventory')}
        </TableCell>
        <TableCell align="center" scope="row" data-cy="row-id">
          {row.amount}
        </TableCell>
        <TableCell align="center" scope="row" data-cy="row-id">
          {row.productId}
        </TableCell>
        <TableCell align="center" scope="row" data-cy="row-id">
          {row.variantId}
        </TableCell>
        <TableCell align="center" scope="row" data-cy="row-id">
          {row.productCode}
        </TableCell>
        <TableCell align="center" scope="row" data-cy="row-id">
          {dkpcs.split(',').length > 3 ? <HtmlTooltip
            title={dkpcs}
          >
            <span>
              <RonadIcons name='plus-circle' size='20px'/>
              {dkpcs.split(',')[0]},
              {dkpcs.split(',')[1]},
              {dkpcs.split(',')[2]}
            </span>
          </HtmlTooltip> : <span>
              {dkpcs.split(',')[0]}
              {dkpcs.split(',')[1]}
              {dkpcs.split(',')[2]}
            </span>}
        </TableCell>
        {/* <TableCell align="center" scope="row" data-cy="row-id">
          {row.fulfillmentVariantCode ?? '---'}
        </TableCell> */}
        {/* <TableCell align="center" scope="row" data-cy="row-id">
          {row.serialNumber}
        </TableCell> */}
        <TableCell align="right">
          {/* <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            disabled={row.inventoryItems.length === 0}
          >
            {open ? (
              <Icon
                path={mdiChevronUp}
                size={1}
                color={
                  row.inventoryItems.length === 0
                    ? '#d4d2da'
                    : theme.palette.secondary.main
                }
              />
            ) : (
              <Icon
                path={mdiChevronDown}
                size={1}
                color={
                  row.inventoryItems.length === 0
                    ? '#d4d2da'
                    : theme.palette.secondary.main
                }
              />
            )}
          </IconButton> */}
        </TableCell>
      </StyledTableRow>
      {/* {open
        ? row.inventoryItems.map((inventoryItem: any) => {
            return (
              <StyledTableRow> */}
      {/* <TableCell>
                  <IconButton
                    aria-label="details"
                    size="small"
                    title="جایگذاری آیتم"
                    data-cy="details"
                    disabled={true}
                  >
                    <Icon
                      path={
                        inventoryItem.status === 'CHECKED'
                          ? mdiCheckBoxOutline
                          : mdiSquareOutline
                      }
                      size={1}
                      color={
                        inventoryItem.status === 'CHECKED' ? theme.palette.green.main : '#383836'
                      }
                    />
                    </IconButton>
                    </TableCell>
                    <TableCell align="center" data-cy="row-id">
                    {translate(inventoryItem.status)}
                  </TableCell> */}
      {/* <TableCell scope="row" align="center">
                  {row.type === 'PRODUCT'
                    ? row.productName
                    : translate(row.type)}
                </TableCell>
                  <TableCell align="center" scope="row" data-cy="row-id">
                    {inventoryItem.amount}
                  </TableCell>
                <TableCell align="center" scope="row" data-cy="row-id">
                  {row.productId}
                </TableCell>
                <TableCell align="center" scope="row" data-cy="row-id">
                  {row.variantId}
                </TableCell>
                <TableCell align="center" scope="row" data-cy="row-id">
                  {row.productCode}
                </TableCell>
                <TableCell align="center" scope="row" data-cy="row-id">
                  {row.variantCode}
                </TableCell>
                <TableCell align="center" scope="row" data-cy="row-id">
                  {row.fulfillmentVariantCode ?? '---'}
                </TableCell> */}
      {/* <TableCell align="center" scope="row" data-cy="row-id">
                  {inventoryItem.serialNumber}
                </TableCell> */}
      {/* <TableCell />
              </StyledTableRow>
            );
          })
        : null} */}
    </>
  );
}
